import type { TierActionLimit } from "./types";

export interface PlanFeatureRow {
  icon: string;
  label: string;
  value: string;
  dim?: boolean;
  green?: boolean;
  /** Key into the feature catalog (/subscriptions/feature-catalog), used to
   * look up the info tooltip's description + "where used" locations. */
  action?: string;
}

// Action metadata shown on pricing/plan pages, per role.
const SELLER_ACTION_META: Record<string, { icon: string; label: string }> = {
  flyer: { icon: "🖼️", label: "AI Flyers" },
  video: { icon: "🎬", label: "AI Videos" },
  itinerary: { icon: "📋", label: "AI Itineraries" },
  connect: { icon: "🤝", label: "Buyer Connects" },
  enquiry: { icon: "📨", label: "Send Enquiries" },
  market_research: { icon: "🔎", label: "Market Research Lookups" },
  webinar: { icon: "🎥", label: "Webinars" },
  deal: { icon: "💼", label: "Deal Listings" },
};

const BUYER_ACTION_META: Record<string, { icon: string; label: string }> = {
  flyer: { icon: "🖼️", label: "AI Flyers" },
  video: { icon: "🎬", label: "AI Videos" },
  itinerary: { icon: "🗺️", label: "AI Itineraries" },
  connect: { icon: "🤝", label: "Seller Connects" },
  enquiry: { icon: "📨", label: "Send Enquiries" },
  market_research: { icon: "🔎", label: "Market Research Lookups" },
};

export const ACTION_META_BY_ROLE: Record<"seller" | "buyer", Record<string, { icon: string; label: string }>> = {
  seller: SELLER_ACTION_META,
  buyer: BUYER_ACTION_META,
};

// Non-quota rows (Feed Boost multiplier) — not driven by admin config.
const STATIC_EXTRAS: Record<"seller" | "buyer", Record<string, PlanFeatureRow[]>> = {
  seller: {
    free:    [{ icon: "📈", label: "Feed Boost", value: "1.00×", action: "feed_boost" }],
    starter: [{ icon: "📈", label: "Feed Boost", value: "1.05×", action: "feed_boost" }],
    mid:     [{ icon: "📈", label: "Feed Boost", value: "1.10×", action: "feed_boost" }],
    premium: [{ icon: "📈", label: "Feed Boost", value: "1.25×", action: "feed_boost" }],
  },
  buyer: {
    free:   [{ icon: "📈", label: "Feed Personalisation", value: "1.00×", action: "feed_boost" }],
    pro:    [{ icon: "📈", label: "Feed Personalisation", value: "1.05×", action: "feed_boost" }],
    studio: [{ icon: "📈", label: "Feed Personalisation", value: "1.10×", action: "feed_boost" }],
    elite:  [{ icon: "📈", label: "Feed Personalisation", value: "1.25×", action: "feed_boost" }],
  },
};

/**
 * limit semantics (daily and monthly alike): -1 means unlimited,
 * 0 means not available on this plan, >0 is the cap for the period.
 */
export function formatLimitValue(limit: number, period: "day" | "month" | "session"): string {
  if (limit < 0) return "Unlimited";
  if (limit === 0) return "Not available";
  if (period === "session") return `${limit} / chat session`;
  return `${limit} / ${period}`;
}

/** Monthly video time is stored in seconds: 90 -> "1m 30s". */
export function formatVideoSeconds(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

export function buildFeatureRows(
  role: "seller" | "buyer",
  tier: string,
  tierLimits: Record<string, TierActionLimit> | undefined,
): PlanFeatureRow[] {
  const meta = ACTION_META_BY_ROLE[role];
  const rows: PlanFeatureRow[] = Object.entries(meta).map(([action, { icon, label }]) => {
    const entry = tierLimits?.[action];
    let value = entry ? formatLimitValue(entry.limit, entry.period) : "—";
    if (action === "video" && entry) {
      // The plan's total video time per month shares the video row.
      const seconds = tierLimits?.video_seconds?.limit;
      if (seconds !== undefined && seconds >= 0) {
        const time = seconds === 0 ? null : formatVideoSeconds(seconds);
        if (entry.limit < 0) value = time ? `${time}/mo` : "—";
        else if (entry.limit > 0) value = time ? `${entry.limit}/mo · ${time}` : "—";
      }
    }
    return {
      icon,
      label,
      value,
      dim: value === "Not available",
      green: value === "Unlimited",
      action,
    };
  });
  return [...rows, ...(STATIC_EXTRAS[role][tier] ?? [])];
}
